import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/membership');
      return;
    }

    pollPaymentStatus(sessionId);
  }, []);

  const pollPaymentStatus = async (sessionId, attemptCount = 0) => {
    if (attemptCount >= maxAttempts) {
      setStatus('timeout');
      toast.error('Payment verification timed out. Please check your email.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Membership activated successfully!');
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
        return;
      } else if (response.data.status === 'expired') {
        setStatus('failed');
        toast.error('Payment session expired');
        return;
      }

      // Continue polling
      setAttempts(attemptCount + 1);
      setTimeout(() => {
        pollPaymentStatus(sessionId, attemptCount + 1);
      }, 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      toast.error('Failed to verify payment');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-3xl p-12 border-2 border-border brutal-shadow text-center"
          >
            {status === 'checking' && (
              <>
                <Loader2 className="w-16 h-16 mx-auto mb-6 text-primary animate-spin" />
                <h1 className="text-3xl font-heading font-bold mb-4">Verifying Payment...</h1>
                <p className="text-muted-foreground mb-4">
                  Please wait while we confirm your payment
                </p>
                <p className="text-sm text-muted-foreground">
                  Attempt {attempts + 1} of {maxAttempts}
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 bg-success rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-12 h-12 text-white" />
                </div>
                <h1 className="text-3xl font-heading font-bold mb-4">Payment Successful!</h1>
                <p className="text-lg text-muted-foreground mb-6">
                  Welcome to XpatSphere! Your membership is now active.
                </p>
                <p className="text-sm text-muted-foreground mb-8">
                  Redirecting to your dashboard...
                </p>
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-full font-bold"
                >
                  Go to Dashboard
                </Button>
              </>
            )}

            {(status === 'failed' || status === 'timeout' || status === 'error') && (
              <>
                <div className="w-20 h-20 bg-destructive rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-4xl text-white">✕</span>
                </div>
                <h1 className="text-3xl font-heading font-bold mb-4">
                  {status === 'timeout' ? 'Verification Timeout' : 'Payment Failed'}
                </h1>
                <p className="text-lg text-muted-foreground mb-8">
                  {status === 'timeout'
                    ? 'Please check your email for confirmation or contact support.'
                    : 'There was an issue processing your payment. Please try again.'}
                </p>
                <Button
                  onClick={() => navigate('/membership')}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-full font-bold"
                >
                  Back to Membership
                </Button>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;